import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import feather from 'feather-icons';
import { useLanguage } from '../../context/LanguageContext';

const MonthlyStatistics = ({ performanceData, reportingType = 'monthly' }) => {
    const { t } = useLanguage();
    const iconRef = useRef(null);

    // Check if data is valid using memoized values
    const isValid = useMemo(
        () => performanceData !== null && performanceData !== undefined,
        [performanceData]
    );
    const isPositive = useMemo(() => isValid && performanceData >= 0, [isValid, performanceData]);
    const isNegative = useMemo(() => isValid && performanceData < 0, [isValid, performanceData]);

    // Memoize message generation to prevent unnecessary recreations
    const message = useMemo(() => {
        if (!isValid) return '';

        const isYearly = reportingType === 'yearly';
        const comparisonText = isYearly ? t('comparedToLastYear') : t('comparedToPreviousMonth');

        if (performanceData < 0) {
            return `${t('earnings')} ${t('dropped')} ${Math.abs(performanceData)}% ${comparisonText}.`;
        } else if (performanceData > 0) {
            return `${t('earnings')} ${t('improved')} ${performanceData}% ${comparisonText}.`;
        } else {
            return isYearly
                ? `${t('earnings')} ${t('remained')} ${t('theSameAsLastYear')}.`
                : `${t('earnings')} ${t('remained')} ${t('theSameAsPreviousMonth')}.`;
        }
    }, [performanceData, t, isValid, reportingType]);

    // Get dynamic CSS classes based on performance
    const getBannerClass = useCallback(() => {
        if (isPositive) return 'statistics-banner statistics-banner--positive';
        if (isNegative) return 'statistics-banner statistics-banner--negative';
        return 'statistics-banner statistics-banner--neutral';
    }, [isPositive, isNegative]);

    // Get dynamic icon based on performance
    const getIconName = useCallback(() => {
        if (isPositive) return 'trending-up';
        if (isNegative) return 'trending-down';
        return 'bar-chart-2';
    }, [isPositive, isNegative]);

    // Get memoized icon name
    const iconName = useMemo(() => getIconName(), [getIconName]);

    // Update icon when iconName changes - directly create and insert SVG
    useEffect(() => {
        if (iconRef.current && feather.icons[iconName]) {
            // Clear previous content
            iconRef.current.innerHTML = '';

            // Create the SVG element from feather icons
            const svgString = feather.icons[iconName].toSvg({
                class: 'icon feather',
            });

            // Insert the SVG into the container
            iconRef.current.insertAdjacentHTML('beforeend', svgString);
        }
    }, [iconName]);

    // Don't render if no valid data
    if (!isValid) {
        return null;
    }

    return (
        <section aria-label={t('monthlyStatistics')}>
            <div className="custom-container">
                <div className={getBannerClass()} role="status" aria-live="polite">
                    <div className="d-flex justify-content-center gap-3">
                        <div className="statistics-image" ref={iconRef}>
                            {/* Icon will be inserted here by useEffect */}
                        </div>
                        <div className="statistics-content d-block">
                            <h5>
                                {reportingType === 'yearly'
                                    ? t('yearlyStatistics')
                                    : t('monthlyStatistics')}
                            </h5>
                            <h6>{message}</h6>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MonthlyStatistics;
